import {
  Building2,
  CalendarDays,
  ChartColumnBig,
  ClipboardList,
  Cpu,
  LayoutDashboard,
  Palmtree,
  Users,
  Wallet,
} from 'lucide-react';
import type { ModuleNav } from '@/components/layout/module-nav';

/**
 * HR's navigation.
 *
 * Kept separate from `AppShell` because each application behind the one
 * sign-in brings its own: the hotel's screens are not HR's screens with more
 * items, and a single shared nav bar has no room for both.
 *
 * `capability` names the flag from `/v1/me` that must be true for an item to
 * appear. `undefined` means everyone who can open this module sees it. The
 * list is driven by what the server says, so the menu cannot offer a screen
 * whose endpoints will 403.
 *
 * Left groups: day-to-day work (month + people). Right group: reference and
 * admin screens, pushed to the trailing edge so they sit next to the account
 * controls rather than between the work items.
 */
export const HR_NAV: ModuleNav = {
  left: [
    [
      { to: '/hr', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      { to: '/hr/timesheet', labelKey: 'nav.timesheet', icon: CalendarDays },
      // Directly under the timesheet: the two are the same month seen twice —
      // hours on one, what those hours cost on the other — and each screen links
      // across to the other carrying the month. Gated on the flag the API gates
      // every `/v1/payroll` route on, so the item cannot lead to a screen of 403s.
      {
        to: '/hr/payroll',
        labelKey: 'nav.payroll',
        icon: Wallet,
        capability: 'canManagePayroll',
      },
      // What the closed payroll months add up to, by department and person.
      { to: '/hr/reports', labelKey: 'nav.reports', icon: ChartColumnBig, capability: 'canSeeReports' },
    ],
    [
      { to: '/hr/employees', labelKey: 'nav.employees', icon: Users },
      { to: '/hr/days-off', labelKey: 'nav.daysOff', icon: Palmtree },
      { to: '/hr/work-logs', labelKey: 'nav.workLogs', icon: ClipboardList },
    ],
  ],
  right: [
    // The path is the route's, with the default tab as a parameter — `to` must be
    // a declared route pattern, not a concrete URL, or typed routing rejects it.
    {
      to: '/hr/reference/$kind',
      params: { kind: 'departments' },
      labelKey: 'nav.referenceData',
      icon: Building2,
    },
    {
      to: '/hr/card-readers',
      labelKey: 'nav.cardReaders',
      icon: Cpu,
      capability: 'canManageReaders',
    },
  ],
};
